// Scan tokens: the `k` on scan and kiosk QR URLs. Signed, bound to one ticket, valid 10 minutes, stateless.
// The WhatsApp token (ticket.token) never travels in a URL: it only lives inside the prefilled message.
// Format: `<expiry base36>.<HMAC-SHA256 tag, 128 bits, base64url>`; the ticket id is in the URL path.

export const SCAN_TOKEN_TTL_MS = 10 * 60_000;

export type ScanCheck = { ok: true; expiresAt: number } | { ok: false; reason: 'expired' | 'invalid' };

/** How a request may open a ticket page: issuing device cookie, live token, or not at all. */
export type ScanAccess = 'device' | 'token' | 'expired' | 'denied';

const enc = new TextEncoder();
const TOKEN_SHAPE = /^([0-9a-z]{1,12})\.([A-Za-z0-9_-]{22})$/;

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function tag(secret: string, ticketId: string, expiresAt: number): Promise<string> {
  const key = await crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const mac = await crypto.subtle.sign('HMAC', key, enc.encode(`scan:${ticketId}:${expiresAt}`));
  return b64url(new Uint8Array(mac).slice(0, 16));
}

function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** A fresh token for this ticket, valid `ttlMs` from `now`. */
export async function issueScanToken(secret: string, ticketId: string, now: number = Date.now(), ttlMs: number = SCAN_TOKEN_TTL_MS): Promise<string> {
  const expiresAt = now + ttlMs;
  return `${expiresAt.toString(36)}.${await tag(secret, ticketId, expiresAt)}`;
}

/** Signature first, then expiry: a forged token is never reported as merely expired. */
export async function verifyScanToken(secret: string, ticketId: string, token: string | null | undefined, now: number = Date.now()): Promise<ScanCheck> {
  const m = token ? TOKEN_SHAPE.exec(token) : null;
  if (!m) return { ok: false, reason: 'invalid' };
  const expiresAt = parseInt(m[1], 36);
  if (!Number.isFinite(expiresAt)) return { ok: false, reason: 'invalid' };
  if (!constantTimeEqual(await tag(secret, ticketId, expiresAt), m[2])) return { ok: false, reason: 'invalid' };
  if (now >= expiresAt) return { ok: false, reason: 'expired' };
  return { ok: true, expiresAt };
}

/** The issuing device may always open its ticket; anyone else needs a live token. */
export function scanAccess(input: { deviceKey: string | null | undefined; ticketDeviceKey: string | null; check: ScanCheck }): ScanAccess {
  if (input.ticketDeviceKey && input.deviceKey && input.deviceKey === input.ticketDeviceKey) return 'device';
  if (input.check.ok) return 'token';
  return input.check.reason === 'expired' ? 'expired' : 'denied';
}
